/*
 * BetterFX's smart ranking, on the real search bar (sections/betterfx-smart.liquid).
 *
 * Search and ranking are the plugin's own (betterfx-desktop/src/search.ts and
 * ranking.ts), the same port as assets/betterfx-try.js: an empty search lists
 * RECENT EFFECTS (the last five used, newest first); while typing, how well a
 * name matches decides first, then favorites (+12000), recent effects
 * (5000 - 250 per place) and how often an effect is used (log2, capped) break
 * the ties. Results group under the plugin's category rows.
 *
 * While on screen a short loop plays: the empty bar with its callouts, a
 * search whose matches first line up A to Z and then slide into BetterFX's
 * order, Enter moving that effect to the top of Recent, then searches for
 * Transform and Crop that rank and apply the same way.
 * Any touch hands it over: star a row (favorites live in the panel's
 * Favorites tab in the plugin), click a row or press Enter to use it, or type,
 * and the list re-ranks live. Off screen or in a hidden tab nothing runs.
 * With reduced motion it starts handed over and nothing slides.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wide = window.matchMedia('(min-width: 990px)');
  const STOP = {};
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  /* ---------- The plugin's search (search.ts) ---------- */
  const normalizeSearch = (value) =>
    value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  const searchWords = (value) => normalizeSearch(value).split(' ').filter(Boolean);
  const searchInitials = (value) => searchWords(value).map((w) => w[0] || '').join('');

  const scoreSubsequence = (query, text) => {
    let qi = 0;
    let prev = -1;
    let gap = 0;
    let run = 0;
    for (let ti = 0; ti < text.length; ti += 1) {
      if (text[ti] !== query[qi]) continue;
      if (prev >= 0 && ti === prev + 1) run += 25;
      else if (prev >= 0) gap += ti - prev - 1;
      else gap += ti;
      prev = ti;
      qi += 1;
      if (qi === query.length) return 5000 + run - gap * 10 - text.length;
    }
    return -1;
  };

  const scoreMatch = (query, text) => {
    const q = normalizeSearch(query);
    const t = normalizeSearch(text);
    if (!q.length) return 100;
    if (t === q) return 10000;
    if (t.startsWith(q)) return 9000 - t.length;
    const words = searchWords(t);
    const wi = words.findIndex((w) => w.startsWith(q));
    if (wi >= 0) return 8500 - wi * 50 - words[wi].length;
    const initials = searchInitials(t);
    if (initials === q) return 8300;
    if (initials.startsWith(q)) return 8200 - initials.length;
    const ci = t.indexOf(q);
    if (ci >= 0) return 7000 - ci * 10 - t.length;
    return scoreSubsequence(q, t);
  };

  const fuzzySearch = (items, query) =>
    items
      .map((item, index) => ({ item, index, score: scoreMatch(query, item.name) }))
      .filter((r) => r.score >= 0)
      .sort((a, b) => (b.score !== a.score ? b.score - a.score : a.index - b.index))
      .map((r) => r.item);

  /* ---------- The plugin's ranking (ranking.ts) ---------- */
  const normalizeRank = (value) => value.toLocaleLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();
  const rankWords = (value) => normalizeRank(value).split(/[\s\-_/\\()[\].]+/).filter(Boolean);
  const rankInitials = (value) => rankWords(value).map((w) => w[0] || '').join('');

  const queryBonus = (name, query) => {
    const n = normalizeRank(name);
    const q = normalizeRank(query);
    if (!q) return 0;
    if (n === q) return 100000;
    if (n.startsWith(q)) return 80000;
    const words = rankWords(name);
    if (words.some((w) => w === q)) return 70000;
    if (words.some((w) => w.startsWith(q))) return 60000;
    const initials = rankInitials(name);
    if (initials === q) return 55000;
    if (initials.startsWith(q)) return 50000;
    if (n.includes(q)) return 40000;
    return 0;
  };

  const KIND_ORDER = { preset: 0, video: 1, audio: 2 };

  const rankEffects = (effects, rawQuery, meta) => {
    const query = rawQuery.trim();
    const fuzzy = fuzzySearch(effects, query);
    const ranked = fuzzy.map((effect, fuzzyIndex) => {
      const recentIndex = meta.recents.indexOf(effect.name);
      const uses = meta.usage[effect.name] || 0;
      const score =
        queryBonus(effect.name, query) +
        Math.max(0, fuzzy.length - fuzzyIndex) * 1000 +
        (meta.favorites.includes(effect.name) ? 12000 : 0) +
        (recentIndex >= 0 ? Math.max(0, 5000 - recentIndex * 250) : 0) +
        (uses > 0 ? Math.min(Math.log2(uses + 1) * 40, 2000) : 0);
      return { effect, score, fuzzyIndex };
    });
    ranked.sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      if (a.fuzzyIndex !== b.fuzzyIndex) return a.fuzzyIndex - b.fuzzyIndex;
      if (a.effect.kind !== b.effect.kind) return KIND_ORDER[a.effect.kind] - KIND_ORDER[b.effect.kind];
      return a.effect.name.localeCompare(b.effect.name, undefined, { sensitivity: 'base', numeric: true });
    });
    return ranked.map((r) => r.effect);
  };

  const CATEGORY = { video: 'VIDEO EFFECTS', audio: 'AUDIO EFFECTS', preset: 'CUSTOM PRESETS' };

  /* ---------- What's installed ---------- */
  const P = (name) => ({ name, kind: 'preset' });
  const V = (name, thirdParty) => ({ name, kind: 'video', thirdParty: !!thirdParty });
  const A = (name) => ({ name, kind: 'audio' });
  const EFFECTS = [
    P('01. Bouncy Slide in (Left)'),
    P('02. Punch Zoom'),
    P('Cinematic Bars'),
    V('Alpha Glow'),
    V('Basic 3D'),
    V('BCC Film Grain', true),
    V('Black & White'),
    V('Brightness & Contrast'),
    V('Camera Blur'),
    V('Channel Blur'),
    V('Corner Pin'),
    V('Crop'),
    V('Directional Blur'),
    V('Drop Shadow'),
    V('Gaussian Blur'),
    V('Horizontal Flip'),
    V('Invert'),
    V('Lens Distortion'),
    V('Lens Flare'),
    V('Mirror'),
    V('Mosaic'),
    V('Noise'),
    V('Posterize'),
    V('S_Glow', true),
    V('S_Shake', true),
    V('Sharpen'),
    V('Transform'),
    V('Twirl'),
    V('Ultra Key'),
    V('Unsharp Mask'),
    V('Vertical Flip'),
    V('Warp Stabilizer'),
    V('Wave Warp'),
    A('Amplify'),
    A('DeEsser'),
    A('DeHummer'),
    A('DeNoise'),
    A('DeReverb'),
    A('Dynamics'),
    A('Hard Limiter'),
    A('Multiband Compressor'),
    A('Parametric Equalizer'),
    A('Pitch Shifter'),
    A('Studio Reverb'),
  ];
  const byName = (name) => EFFECTS.find((e) => e.name === name);
  // Five rows under one category row; a second category row costs a row, so
  // the bar never outgrows its figure (sections/betterfx-smart.liquid).
  const shown = (rows) => {
    const top = rows.slice(0, VISIBLE_ROWS);
    return new Set(top.map((e) => e.kind)).size > 1 ? top.slice(0, VISIBLE_ROWS - 1) : top;
  };

  // The plugin keeps 10 recent effects (metadata.ts) and shows 5 of them.
  const MAX_RECENTS = 10;
  const SHOW_RECENTS = 5;
  const VISIBLE_ROWS = 5;
  // An effect gets the "Most used" callout when it's the most used one on show
  // and has been used at least this often.
  const USED_MIN = 5;

  const META = () => ({
    recents: ['Warp Stabilizer', 'Crop', 'Transform', 'Gaussian Blur', 'DeNoise', 'S_Shake', 'Drop Shadow', 'Horizontal Flip', 'Ultra Key'],
    usage: { 'Warp Stabilizer': 14, Crop: 9, DeNoise: 7, S_Shake: 6, Transform: 5, 'Drop Shadow': 4, 'Gaussian Blur': 3, 'Horizontal Flip': 2, 'Ultra Key': 2 },
    favorites: ['Gaussian Blur'],
  });

  // What the plugin shows for a search: the ranked list, the leading kind first.
  const resultsFor = (query, meta) => {
    if (!query) return meta.recents.map(byName).filter(Boolean).slice(0, SHOW_RECENTS);
    const ranked = rankEffects(EFFECTS, query, meta);
    const lead = ranked[0] && ranked[0].kind;
    return lead ? ranked.filter((e) => e.kind === lead).concat(ranked.filter((e) => e.kind !== lead)) : [];
  };

  // The same rows in plain A to Z order (kept under the same category rows).
  const plainOrder = (list) => {
    const groups = [];
    list.forEach((e) => {
      if (!groups.includes(e.kind)) groups.push(e.kind);
    });
    return list
      .slice()
      .sort((a, b) =>
        a.kind !== b.kind
          ? groups.indexOf(a.kind) - groups.indexOf(b.kind)
          : a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true })
      );
  };

  const easeMove = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  const ICON = {
    recent:
      '<svg class="bm-mk-recent" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6.4" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M8 4.6V8l2.3 1.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    used:
      '<svg class="bm-mk-used" viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="9.5" width="3" height="5" rx="1" fill="currentColor"/><rect x="6.5" y="6" width="3" height="8.5" rx="1" fill="currentColor"/><rect x="11.5" y="2" width="3" height="12.5" rx="1" fill="currentColor"/></svg>',
    star: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.6l1.9 4 4.4.56-3.23 3.04.82 4.36L8 11.4l-3.9 2.16.83-4.36L1.7 6.16l4.4-.56Z"/></svg>',
  };

  const init = (root) => {
    if (root.dataset.bmReady) return;
    const q = (sel) => root.querySelector(sel);
    const fig = q('[data-bm-fig]');
    const bar = q('[data-bm-bar]');
    const input = q('[data-bm-input]');
    const caret = q('[data-bm-caret]');
    const results = q('[data-bm-results]');
    const annot = q('[data-bm-annot]');
    const svg = q('[data-bm-lines]');
    const cap = q('[data-bm-cap]');
    const copyEl = q('[data-bm-copy]');
    if (!fig || !bar || !input || !results || !annot || !svg || !cap || !copyEl) return;
    root.dataset.bmReady = 'true';

    let copy = {};
    try {
      copy = JSON.parse(copyEl.textContent);
    } catch (e) {
      copy = {};
    }
    const say = (key, name) => String(copy[key] || '').replace(/\[effect\]/g, name || '');

    const paths = {};
    const dots = {};
    const pills = {};
    const legend = {};
    ['recent', 'used', 'fav'].forEach((k) => {
      paths[k] = svg.querySelector(`[data-bm-path="${k}"]`);
      dots[k] = svg.querySelector(`[data-bm-dot="${k}"]`);
      pills[k] = q(`[data-bm-pill="${k}"]`);
      legend[k] = q(`[data-bm-legend="${k}"]`);
    });

    /* ---------- State ---------- */
    let meta = META();
    let query = '';
    let list = []; // the effects on show, in order
    let selected = 0;
    let mode = 'auto'; // 'auto' plays the loop, 'hands' is the visitor's
    let visible = false;
    let playing = false;
    let run = 0;
    const reveal = new Set(['recent', 'used', 'fav']);
    let targets = { recent: [], used: null, fav: null };

    /* ---------- Geometry ---------- */
    const geo = { k: 1, rowH: 40, catH: 26, top: 0, barL: 0, barR: 0, w: 0, h: 0, callouts: false, pill: {} };
    const measure = () => {
      geo.k = parseFloat(getComputedStyle(fig).getPropertyValue('--k')) || 1;
      // whole pixels, so rows butt up without hairline seams
      geo.rowH = Math.round(40 * geo.k);
      geo.catH = Math.round(26 * geo.k);
      const f = fig.getBoundingClientRect();
      const b = bar.getBoundingClientRect();
      const r = results.getBoundingClientRect();
      geo.w = f.width;
      geo.h = f.height;
      geo.barL = b.left - f.left;
      geo.barR = b.right - f.left;
      geo.top = r.top - f.top + results.clientTop;
      geo.callouts = wide.matches;
      Object.keys(pills).forEach((k) => {
        if (pills[k]) geo.pill[k] = { w: pills[k].offsetWidth, h: pills[k].offsetHeight || 40 };
      });
      annot.style.setProperty('--bl', `${geo.barL}px`);
      annot.style.setProperty('--br', `${geo.barR}px`);
      svg.setAttribute('width', String(Math.max(1, Math.round(geo.w))));
      svg.setAttribute('height', String(Math.max(1, Math.round(geo.h))));
      svg.setAttribute('viewBox', `0 0 ${Math.max(1, geo.w)} ${Math.max(1, geo.h)}`);
    };

    /* ---------- Rows: every row is placed by the script so it can slide ---------- */
    const items = new Map(); // key -> { el, arow, star, y, y0, y1, o, o0, o1, h, removing }
    let resH = { v: 0, from: 0, to: 0 };
    let tween = { start: 0, dur: 0, raf: 0 };

    const rowHtml = (effect) => {
      const tag = effect.thirdParty ? copy.tag_third || '3rd party' : effect.kind === 'preset' ? copy.tag_preset || 'custom preset' : '';
      return `<span class="bfx-name">${esc(effect.name)}</span>${tag ? `<span class="bfx-tag">${esc(tag)}</span>` : ''}`;
    };

    const makeItem = (entry) => {
      const el = document.createElement('div');
      const item = { el, key: entry.key, type: entry.type, y: 0, y0: 0, y1: 0, o: 0, o0: 0, o1: 1, h: entry.h, removing: false };
      if (entry.type === 'row') {
        el.className = 'bfx-item bfx-row';
        el.addEventListener('animationend', () => el.classList.remove('is-risen'));
        el.setAttribute('role', 'option');
        el.dataset.name = entry.effect.name;
        el.innerHTML = rowHtml(entry.effect);
        const arow = document.createElement('div');
        arow.className = 'bm-arow';
        arow.innerHTML =
          `<span class="bm-mk" aria-hidden="true">${ICON.used}${ICON.recent}</span>` +
          `<button type="button" class="bm-star" aria-pressed="false" aria-label="${esc(copy.star || 'Favorite')}: ${esc(entry.effect.name)}">${ICON.star}</button>`;
        item.arow = arow;
        item.star = arow.querySelector('.bm-star');
        item.star.dataset.name = entry.effect.name;
        annot.appendChild(arow);
      } else if (entry.type === 'cat') {
        el.className = 'bfx-item bfx-cat';
        el.textContent = entry.label;
      } else {
        el.className = 'bfx-item bfx-empty';
        el.textContent = copy.empty || 'No effects found';
      }
      return item;
    };

    const paintItem = (item) => {
      const t = `translate3d(0, ${item.y.toFixed(2)}px, 0)`;
      item.el.style.transform = t;
      item.el.style.opacity = item.o.toFixed(3);
      if (item.arow) {
        item.arow.style.transform = `translate3d(0, ${(geo.top + item.y).toFixed(2)}px, 0)`;
        item.arow.style.opacity = item.o.toFixed(3);
        item.arow.style.visibility = item.o < 0.05 ? 'hidden' : '';
      }
    };

    const centerOf = (key) => {
      const item = items.get(key);
      return item ? geo.top + item.y + geo.rowH / 2 : null;
    };

    /* ---------- Callouts ---------- */
    const pillAt = (k, x, y, on) => {
      const pill = pills[k];
      if (!pill) return;
      pill.classList.toggle('is-on', on);
      if (y == null) return;
      const h = (geo.pill[k] && geo.pill[k].h) || 40;
      pill.style.transform = `translate3d(${x.toFixed(1)}px, ${(y - h / 2).toFixed(1)}px, 0)`;
    };

    const drawCallouts = () => {
      if (!geo.callouts) return;
      // Recent: a comb along the recent rows, its pill at the middle.
      const ys = targets.recent.map(centerOf).filter((y) => y != null);
      const recentOn = reveal.has('recent') && ys.length > 0;
      if (ys.length) {
        const combX = geo.barL - 20;
        const tickX = geo.barL - 9;
        const pillR = combX - 30;
        const top = Math.min(...ys);
        const bottom = Math.max(...ys);
        const mid = (top + bottom) / 2;
        let d = `M${combX} ${top}V${bottom}`;
        ys.forEach((y) => (d += `M${combX} ${y}H${tickX}`));
        d += `M${pillR} ${mid}H${combX}`;
        paths.recent.setAttribute('d', d);
        pillAt('recent', pillR, mid, recentOn);
      } else pillAt('recent', 0, null, false);
      paths.recent.classList.toggle('is-on', recentOn);

      // Most used and Favorite: a line from just right of the row to the pill.
      const x0 = geo.barR + 38;
      const pillL = geo.barR + 82;
      const right = ['used', 'fav']
        .map((k) => ({ k, y: targets[k] ? centerOf(targets[k]) : null }))
        .filter((p) => p.y != null)
        .sort((a, b) => a.y - b.y);
      const gap = 50;
      right.forEach((p, i) => (p.py = i ? Math.max(p.y, right[i - 1].py + gap) : p.y));
      const maxY = geo.h - 24;
      if (right.length && right[right.length - 1].py > maxY) {
        const shift = right[right.length - 1].py - maxY;
        right.forEach((p) => (p.py -= shift));
      }
      ['used', 'fav'].forEach((k) => {
        const p = right.find((r) => r.k === k);
        const on = !!p && reveal.has(k);
        paths[k].classList.toggle('is-on', on);
        dots[k].classList.toggle('is-on', on);
        if (!p) return pillAt(k, pillL, null, false);
        const c = (pillL - x0) / 2;
        paths[k].setAttribute('d', `M${x0} ${p.y}C${x0 + c} ${p.y} ${pillL - c} ${p.py} ${pillL} ${p.py}`);
        dots[k].setAttribute('cx', String(x0));
        dots[k].setAttribute('cy', String(p.y));
        pillAt(k, pillL, p.py, on);
      });
    };

    // Small icons beside each row (phones and tablets) follow the same targets.
    const markRows = () => {
      items.forEach((item) => {
        if (!item.arow) return;
        const name = item.el.dataset.name;
        item.arow.classList.toggle('is-recent', reveal.has('recent') && targets.recent.includes(item.key));
        item.arow.classList.toggle('is-used', reveal.has('used') && targets.used === item.key);
        const fav = meta.favorites.includes(name);
        item.star.setAttribute('aria-pressed', String(fav));
      });
    };

    const frame = (now) => {
      const t = tween.dur ? Math.min(1, (now - tween.start) / tween.dur) : 1;
      const e = easeMove(t);
      items.forEach((item) => {
        item.y = item.y0 + (item.y1 - item.y0) * e;
        // leaving rows fade out quickly, new ones fade in once there's room
        const f = item.o1 < item.o0 ? Math.min(1, t * 3) : item.o1 > item.o0 ? Math.max(0, (t - 0.3) / 0.7) : 1;
        item.o = item.o0 + (item.o1 - item.o0) * f;
        paintItem(item);
      });
      resH.v = resH.from + (resH.to - resH.from) * e;
      results.style.height = `${resH.v.toFixed(2)}px`;
      drawCallouts();
      if (t < 1) tween.raf = requestAnimationFrame(frame);
      else {
        tween.raf = 0;
        items.forEach((item, key) => {
          if (!item.removing) return;
          item.el.remove();
          if (item.arow) item.arow.remove();
          items.delete(key);
        });
      }
    };

    const animate = (dur) => {
      cancelAnimationFrame(tween.raf);
      tween.dur = still.matches ? 0 : dur;
      tween.start = performance.now();
      if (!tween.dur) frame(tween.start);
      else tween.raf = requestAnimationFrame(frame);
    };

    // Lay the list out; rows already on show slide to their new place.
    const layout = (newList, dur = 520) => {
      list = shown(newList);
      selected = 0;
      const entries = [];
      let y = 0;
      const push = (entry) => {
        entry.y = y;
        y += entry.h;
        entries.push(entry);
      };
      if (!query && list.length) push({ key: 'c:recent', type: 'cat', label: copy.cat_recent || 'RECENT EFFECTS', h: geo.catH });
      if (query && !list.length) push({ key: 'empty', type: 'empty', h: Math.round(66 * geo.k) });
      let prev = null;
      list.forEach((effect, i) => {
        if (query && effect.kind !== prev) {
          push({ key: `c:${effect.kind}`, type: 'cat', label: copy[`cat_${effect.kind}`] || CATEGORY[effect.kind], h: geo.catH });
          prev = effect.kind;
        }
        push({ key: `r:${effect.name}`, type: 'row', effect, index: i, h: geo.rowH });
      });

      const keep = new Set(entries.map((e) => e.key));
      items.forEach((item, key) => {
        if (keep.has(key)) return;
        item.removing = true;
        item.y0 = item.y;
        item.y1 = item.y;
        item.o0 = item.o;
        item.o1 = 0;
        item.el.style.zIndex = '0';
        if (item.arow) item.arow.style.pointerEvents = 'none';
      });
      entries.forEach((entry) => {
        let item = items.get(entry.key);
        if (!item || item.removing) {
          if (item) {
            item.el.remove();
            if (item.arow) item.arow.remove();
          }
          item = makeItem(entry);
          // a new row slides out from under the row above it
          item.y = entry.y - (dur && !still.matches && entry.type === 'row' ? geo.rowH * 0.5 : 0);
          item.o = 0;
          item.fresh = true;
          items.set(entry.key, item);
        }
        item.removing = false;
        item.h = entry.h;
        item.el.style.height = `${entry.h}px`;
        if (item.arow) item.arow.style.height = `${entry.h}px`;
        item.y0 = item.y;
        item.y1 = entry.y;
        item.o0 = item.o;
        item.o1 = 1;
        if (item.arow) item.arow.style.pointerEvents = '';
        if (entry.type === 'row') item.el.style.zIndex = item.fresh ? '0' : item.y1 < item.y0 - 1 ? '2' : '1';
        item.fresh = false;
        if (entry.type === 'row') {
          item.index = entry.index;
          const on = entry.index === selected;
          item.el.classList.toggle('is-selected', on);
          item.el.setAttribute('aria-selected', String(on));
          item.el.dataset.row = String(entry.index);
        }
      });
      // Keep reading order = visual order, moving only what has to move
      // (moving a node restarts its animations).
      const leaving = new Set();
      items.forEach((item) => item.removing && leaving.add(item.el));
      let cursor = results.firstChild;
      entries.forEach((entry) => {
        const el = items.get(entry.key).el;
        while (cursor && leaving.has(cursor)) cursor = cursor.nextSibling;
        if (cursor === el) cursor = cursor.nextSibling;
        else results.insertBefore(el, cursor);
      });
      resH.from = resH.v;
      resH.to = y;

      // Which rows each callout points at.
      const rowKeys = list.map((e) => `r:${e.name}`);
      targets = { recent: [], used: null, fav: null };
      let most = USED_MIN - 1;
      list.forEach((e, i) => {
        if (meta.recents.includes(e.name)) targets.recent.push(rowKeys[i]);
        const uses = meta.usage[e.name] || 0;
        if (uses > most) {
          most = uses;
          targets.used = rowKeys[i];
        }
        if (!targets.fav && meta.favorites.includes(e.name)) targets.fav = rowKeys[i];
      });
      markRows();
      animate(dur);
    };

    const updateSelected = () => {
      items.forEach((item) => {
        if (item.type !== 'row' || item.removing) return;
        const on = item.index === selected;
        item.el.classList.toggle('is-selected', on);
        item.el.setAttribute('aria-selected', String(on));
      });
    };

    /* ---------- Feedback ---------- */
    const litTimers = {};
    const light = (k) => {
      if (still.matches) return;
      [pills[k], paths[k], dots[k], legend[k]].forEach((el) => el && el.classList.add('is-lit'));
      clearTimeout(litTimers[k]);
      litTimers[k] = setTimeout(() => {
        [pills[k], paths[k], dots[k], legend[k]].forEach((el) => el && el.classList.remove('is-lit'));
      }, 1600);
    };

    const glow = (name) => {
      const item = items.get(`r:${name}`);
      if (!item || still.matches) return;
      item.el.classList.remove('is-risen');
      void item.el.offsetWidth; // restart the glow
      item.el.classList.add('is-risen');
    };

    let capTimer = 0;
    let backTimer = 0;
    const caption = (text) => {
      clearTimeout(backTimer);
      if (cap.textContent === text) return;
      clearTimeout(capTimer);
      if (still.matches) {
        cap.textContent = text;
        return;
      }
      cap.classList.add('is-out');
      capTimer = setTimeout(() => {
        cap.textContent = text;
        cap.classList.remove('is-out');
      }, 190);
    };

    // Re-rank what's on show; light the callout behind the row that rose most.
    const rerank = (dur = 640) => {
      const before = list.map((e) => e.name);
      layout(resultsFor(query, meta), dur);
      let best = null;
      list.forEach((e, i) => {
        const was = before.indexOf(e.name);
        if (was > i && (!best || was - i > best.by)) best = { name: e.name, by: was - i };
      });
      if (!best) return null;
      glow(best.name);
      // light every callout that points at the row that rose
      const key = `r:${best.name}`;
      if (targets.fav === key) light('fav');
      if (targets.recent.includes(key)) light('recent');
      if (targets.used === key) light('used');
      return best.name;
    };

    /* ---------- Typing ---------- */
    let measureCtx = null;
    const placeCaret = () => {
      if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
      const cs = getComputedStyle(input);
      measureCtx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      const w = input.value ? measureCtx.measureText(input.value).width + 1 : 0;
      caret.style.setProperty('--cx', `${(input.offsetLeft + w).toFixed(1)}px`);
    };

    const setQuery = (value) => {
      input.value = value;
      query = value.trim();
      placeCaret();
    };

    /* ---------- Using an effect (Enter or click) ---------- */
    const remember = (name) => {
      meta.recents = [name].concat(meta.recents.filter((n) => n !== name)).slice(0, MAX_RECENTS);
      meta.usage[name] = (meta.usage[name] || 0) + 1;
    };

    let applyTimer = 0;
    const apply = (effect) => {
      if (!effect) return;
      glow(effect.name);
      const before = resultsFor('', meta);
      const fromRecents = !query;
      remember(effect.name);
      flash(say('applied', effect.name));
      // The plugin hides on Enter and opens on an empty search next time:
      // show Recent as it was, then let the used effect slide to the top.
      const reopen = () => {
        setQuery('');
        if (!fromRecents) layout(before, 0);
        const slide = () => {
          layout(resultsFor('', meta), 700);
          glow(effect.name);
          light('recent');
        };
        if (fromRecents || still.matches) slide();
        else requestAnimationFrame(() => requestAnimationFrame(slide));
      };
      clearTimeout(applyTimer);
      if (still.matches) return reopen();
      bar.classList.remove('is-dip');
      void bar.offsetWidth;
      bar.classList.add('is-dip');
      applyTimer = setTimeout(reopen, fromRecents ? 120 : 190);
    };

    const toggleFavorite = (name, star) => {
      const on = !meta.favorites.includes(name);
      meta.favorites = on ? meta.favorites.concat(name) : meta.favorites.filter((n) => n !== name);
      if (star && !still.matches) {
        star.classList.remove('is-pop');
        void star.offsetWidth;
        star.classList.add('is-pop');
      }
      const moved = rerank(640);
      if (on) light('fav');
      if (!moved && on) glow(name);
      flash(say(on ? 'starred' : 'unstarred', name));
    };

    // What a visitor just did (used, starred, unstarred) shows briefly, then
    // the line goes back to the invitation.
    const flash = (text) => {
      caption(text);
      if (mode !== 'hands') return;
      backTimer = setTimeout(() => caption(say('turn')), 1800);
    };

    /* ---------- The loop ---------- */
    const wait = (r, ms) =>
      new Promise((resolve, reject) => {
        setTimeout(() => (r === run && playing && visible && !document.hidden ? resolve() : reject(STOP)), ms);
      });

    const typeText = async (r, text) => {
      bar.classList.add('is-typing');
      for (const ch of text) {
        setQuery(input.value + ch);
        // the plugin redraws its list at once on every keystroke
        layout(plainOrder(shown(resultsFor(query, meta))), 0);
        await wait(r, 120 + Math.random() * 60);
      }
    };

    const setMode = (m) => {
      mode = m;
      root.classList.toggle('is-auto', m === 'auto');
      root.classList.toggle('is-hands', m === 'hands');
      cap.setAttribute('aria-live', m === 'hands' ? 'polite' : 'off');
    };

    const play = async () => {
      const r = ++run;
      playing = true;
      setMode('auto');
      try {
        for (;;) {
          meta = META();
          bar.classList.remove('is-typing', 'is-dip');
          setQuery('');
          reveal.clear();
          layout(resultsFor('', meta), 0);
          caption(say('open'));
          fig.classList.remove('is-fade');
          await wait(r, 250);
          for (const k of ['recent', 'used', 'fav']) {
            reveal.add(k);
            markRows();
            drawCallouts();
            light(k);
            await wait(r, 380);
          }
          await wait(r, 1100);

          // A search: A to Z first, then BetterFX's order.
          caption(say('plain'));
          await typeText(r, 'blur');
          await wait(r, 800);
          rerank(600);
          caption(say('favorite'));
          await wait(r, 2000);

          // Enter: it goes to the top of Recent.
          bar.classList.remove('is-typing');
          apply(list[selected]);
          await wait(r, 2200);

          // More searches: what you use often comes first, and Enter uses it.
          for (const q of ['tr', 'cr']) {
            caption(say('plain'));
            bar.classList.add('is-typing');
            setQuery('');
            await typeText(r, q);
            await wait(r, 800);
            rerank(600);
            caption(say('often'));
            await wait(r, 1700);
            bar.classList.remove('is-typing');
            apply(list[selected]);
            await wait(r, 2000);
          }

          fig.classList.add('is-fade');
          await wait(r, 450);
        }
      } catch (e) {
        if (e !== STOP) throw e;
      }
    };

    // Stop the loop where it is (leaving the screen or a hidden tab).
    const pause = () => {
      run += 1;
      playing = false;
      clearTimeout(applyTimer);
      bar.classList.remove('is-typing');
      fig.classList.remove('is-fade');
    };

    // Hand it over: keep what's on show, ranked for real.
    const handOver = () => {
      if (mode === 'hands') return;
      pause();
      setMode('hands');
      ['recent', 'used', 'fav'].forEach((k) => reveal.add(k));
      layout(resultsFor(query, meta), 420);
      caption(say('turn'));
    };

    const restart = () => {
      if (still.matches) {
        meta = META();
        setQuery('');
        layout(resultsFor('', meta), 0);
        caption(say('turn'));
        return;
      }
      pause();
      if (visible && !document.hidden) play();
      else setMode('auto');
    };

    /* ---------- The visitor ---------- */
    fig.addEventListener('click', handOver);
    input.addEventListener('focus', handOver);
    input.addEventListener('input', () => {
      handOver();
      query = input.value.trim();
      layout(resultsFor(query, meta), 0);
    });
    input.addEventListener('keydown', (event) => {
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!list.length) return;
        selected = (selected + (event.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length;
        updateSelected();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        apply(list[selected]);
      } else if (event.key === 'Escape' && input.value) {
        event.preventDefault();
        setQuery('');
        layout(resultsFor('', meta), 0);
      }
    });

    results.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' || mode !== 'hands') return;
      const row = event.target.closest('[data-row]');
      if (!row) return;
      const i = Number(row.dataset.row);
      if (i !== selected) {
        selected = i;
        updateSelected();
      }
    });
    results.addEventListener('click', (event) => {
      const row = event.target.closest('[data-row]');
      if (!row) return;
      handOver();
      apply(list[Number(row.dataset.row)]);
    });
    annot.addEventListener('click', (event) => {
      const star = event.target.closest('.bm-star');
      if (!star) return;
      handOver();
      toggleFavorite(star.dataset.name, star);
    });

    root.querySelectorAll('[data-bm-chip]').forEach((chip) =>
      chip.addEventListener('click', () => {
        handOver();
        setQuery(chip.dataset.bmChip);
        layout(resultsFor(query, meta), 0);
      })
    );
    const tryBtn = q('[data-bm-try]');
    if (tryBtn) tryBtn.addEventListener('click', handOver);
    const replayBtn = q('[data-bm-replay]');
    if (replayBtn)
      replayBtn.addEventListener('click', () => {
        if (still.matches) return restart();
        setMode('auto');
        restart();
      });

    /* ---------- Start ---------- */
    measure();
    results.innerHTML = '';
    root.classList.add('is-live', 'is-ready');
    layout(resultsFor('', meta), 0);
    if (still.matches) {
      setMode('hands');
      caption(say('turn'));
    } else setMode('auto');

    if ('ResizeObserver' in window) {
      let last = '';
      new ResizeObserver(() => {
        const sizeKey = `${fig.clientWidth}x${fig.clientHeight}`;
        if (sizeKey === last) return;
        last = sizeKey;
        const k = geo.k;
        measure();
        if (k !== geo.k) layout(list, 0); // rows changed size: lay out again at once
        else {
          items.forEach(paintItem);
          drawCallouts();
        }
        placeCaret();
      }).observe(fig);
    }

    const wake = () => {
      if (mode === 'auto' && !playing && visible && !document.hidden && !still.matches) play();
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visible = entry.isIntersecting;
            if (visible) wake();
            else if (mode === 'auto') pause();
          });
        },
        { threshold: 0.2 }
      ).observe(fig);
    } else {
      setMode('hands');
      caption(say('turn'));
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (mode === 'auto') pause();
      } else wake();
    });
  };

  const boot = () => document.querySelectorAll('[data-bm-demo]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // The theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', boot);
})();
